import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FolderGit2, Link2, FileText, Inbox, Search, Eye, Pencil } from 'lucide-react';
import { useCrud, usePagination } from '../../hooks/useApi';
import { processService } from '../../services/api';
import toast from 'react-hot-toast';
import FormModalLayout from '../../components/Common/FormModalLayout';
import FormPageShell from '../../components/Common/FormPageShell';
import FormSection from '../../components/Common/FormSection';
import PageHeader from '../../components/Common/PageHeader';
import QueryErrorBanner from '../../components/Common/QueryErrorBanner';
import Can from '../../components/Common/Can';
import ProcessoTable from '../../components/Process/ProcessoTable';
import ProcessoVinculoFields, { blankVinculo } from '../../components/Process/ProcessoVinculoFields';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import { ensureRichTextValue, isEmptyRichText, RichTextEditor } from '../../components/RichTextEditor';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';
import { useTenant } from '../../contexts/TenantContext';
import { isConsultoriaTenant, isRequesterRole, partyNoun } from '../../utils/tenantProfile';
import { useAuth } from '../../contexts/AuthContext';

const SITUACAO_LABELS = { 1: 'Aberto', 2: 'Em Andamento', 3: 'Finalizado', 4: 'Arquivado' };
const TIPO_LABELS = { 1: 'Ambiental', 2: 'Alvará', 3: 'Habite-se', 4: 'Diversos' };

const Processos = () => {
  const { t } = useTranslation('process');
  const { tenant } = useTenant();
  const { user } = useAuth();
  const consultoria = isConsultoriaTenant(tenant);
  const requester = isRequesterRole(user);
  const party = partyNoun(tenant);

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [search, setSearch] = useState('');
  const [filterSit, setFilterSit] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const pagination = usePagination(1, 10);
  const [form, setForm] = useState({
    numero: '',
    situacao: 1,
    tipo: 1,
    orgao_origem_id: '',
    setor_origem_id: '',
    data_abertura: new Date().toISOString().slice(0, 10),
    usuario_id: '',
    requerente_id: '',
    descricao: '',
  });

  const { useList, useCreate, useUpdate, useDelete } = useCrud('processos', processService.processoCrud);

  const { data: listData, isLoading, error } = useList();
  const items = Array.isArray(listData) ? listData : listData?.data ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      const sitOk = !filterSit || String(item.situacao) === String(filterSit);
      const tipoOk = !filterTipo || String(item.tipo) === String(filterTipo);
      const text = `${item.numero || ''} ${item.descricao || ''}`.toLowerCase();
      const searchOk = !q || text.includes(q);
      return sitOk && tipoOk && searchOk;
    });
  }, [items, search, filterSit, filterTipo]);
  const pagedItems = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return filtered.slice(start, start + pagination.limit);
  }, [filtered, pagination.page, pagination.limit]);

  useEffect(() => {
    pagination.setTotal(filtered.length);
    pagination.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset when the filtered set changes
  }, [filtered.length, filterSit, filterTipo, search]);

  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const handleCreate = () => {
    setEditingItem(null);
    setForm({
      numero: '',
      situacao: 1,
      tipo: 1,
      orgao_origem_id: '',
      setor_origem_id: '',
      data_abertura: new Date().toISOString().slice(0, 10),
      usuario_id: '',
      requerente_id: '',
      descricao: '',
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setForm({
      numero: item.numero || '',
      situacao: item.situacao ?? 1,
      tipo: item.tipo ?? 1,
      orgao_origem_id: item.orgao_origem_id || '',
      setor_origem_id: item.setor_origem_id || '',
      data_abertura: (item.data_abertura || '').slice(0, 10),
      usuario_id: item.usuario_id || '',
      requerente_id: item.requerente_id || '',
      descricao: ensureRichTextValue(item.descricao || ''),
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja excluir este processo?')) {
      deleteMutation.mutate(id, {
        onSuccess: () => toast.success('Excluído com sucesso'),
        onError: (e) => toast.error(userFacingError(e, 'deleteError')),
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.numero.trim()) {
      toast.error(t('numberRequired'));
      return;
    }
    const payload = {
      ...form,
      numero: form.numero.trim(),
      orgao_origem_id: blankVinculo(form.orgao_origem_id),
      setor_origem_id: blankVinculo(form.setor_origem_id),
      usuario_id: blankVinculo(form.usuario_id),
      requerente_id: blankVinculo(form.requerente_id),
      descricao: isEmptyRichText(form.descricao) ? null : (form.descricao || '').trim(),
    };
    if (editingItem) {
      updateMutation.mutate(
        { id: editingItem.id, data: payload },
        {
          onSuccess: () => {
            toast.success('Processo atualizado');
            setShowModal(false);
            setEditingItem(null);
          },
          onError: (err) => toast.error(userFacingError(err, 'updateError')),
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toast.success('Processo criado');
          setShowModal(false);
        },
        onError: (err) => toast.error(userFacingError(err, 'createError')),
      });
    }
  };

  return (
    <FormPageShell maxWidth="w-full max-w-none" className="!p-0">
      <div className="space-y-6">
        <PageHeader
          title={t("title")}
          description={
            requester
              ? 'Consulta dos processos dos seus empreendimentos. Novos pedidos nascem em Requerimentos.'
              : 'Lista, filtros e cadastro de processos ambientais, alvarás e habite-se.'
          }
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Processos' },
          ]}
        >
          {consultoria || requester ? null : (
            <Link to="/process/routing" className="btn btn-outline inline-flex items-center gap-2">
              <Inbox className="h-4 w-4" />
              {t('inboxTitle')}
            </Link>
          )}
          {requester ? null : (
          <Can permission={P.PROCESS_ACCESS}>
            <button type="button" onClick={handleCreate} className="btn btn-primary inline-flex items-center gap-2 shadow-sm">
              <Plus className="h-4 w-4" />
              Novo processo
            </button>
          </Can>
          )}
        </PageHeader>

        {error ? <QueryErrorBanner error={error} /> : null}

        <div className="data-list-card overflow-visible">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="form-group lg:col-span-2">
                <label className="form-label" htmlFor="proc-search">Busca</label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    id="proc-search"
                    className="form-input pl-9"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Número do processo"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="proc-sit">Situação</label>
                <select id="proc-sit" className="form-select" value={filterSit} onChange={(e) => setFilterSit(e.target.value)}>
                  <option value="">Todas</option>
                  {Object.entries(SITUACAO_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="proc-tipo">Tipo</label>
                <select id="proc-tipo" className="form-select" value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)}>
                  <option value="">Todos</option>
                  {Object.entries(TIPO_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="space-y-4 p-5 sm:p-6">
            {isLoading ? (
              <p className="text-sm text-gray-600" role="status">Carregando processos…</p>
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Tabela
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('cards')}
                  className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-outline'} btn-sm`}
                >
                  Cards
                </button>
              </div>
              <div className="text-sm text-gray-600">
                Mostrando {filtered.length} processo(s) {items.length !== filtered.length ? '(filtrados)' : ''}
              </div>
            </div>

            {isLoading ? null : viewMode === 'table' ? (
              <ProcessoTable
                items={pagedItems}
                totalCount={filtered.length}
                pagination={pagination}
                situacaoLabels={SITUACAO_LABELS}
                tipoLabels={TIPO_LABELS}
                onEdit={requester ? undefined : handleEdit}
                onDelete={requester ? undefined : handleDelete}
                onCreate={requester ? undefined : handleCreate}
                requester={requester}
              />
            ) : !filtered.length ? (
              <ProcessoTable
                items={[]}
                situacaoLabels={SITUACAO_LABELS}
                tipoLabels={TIPO_LABELS}
                onCreate={requester ? undefined : handleCreate}
                requester={requester}
              />
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {pagedItems.map((item) => (
                  <div key={item.id} className="card">
                    <div className="card-content">
                      <div className="mb-4 flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{item.numero || item.id?.slice(0, 8)}</h3>
                          <p className="text-sm text-gray-600">{TIPO_LABELS[item.tipo] ?? item.tipo}</p>
                        </div>
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            item.situacao === 1
                              ? 'bg-sky-100 text-sky-900'
                              : item.situacao === 2
                                ? 'bg-amber-100 text-amber-950'
                                : item.situacao === 3
                                  ? 'bg-emerald-100 text-emerald-900'
                                  : 'bg-slate-100 text-slate-800'
                          }`}
                        >
                          {SITUACAO_LABELS[item.situacao] ?? item.situacao}
                        </span>
                      </div>
                      <p className="mb-4 text-sm text-gray-500">
                        Abertura: {item.data_abertura ? String(item.data_abertura).slice(0, 10) : '—'}
                      </p>
                      <div className="flex gap-2">
                        <Link to={`/process/cases/${item.id}`} className="btn btn-outline btn-sm flex-1">
                          <Eye className="mr-1 h-4 w-4" />
                          Ver
                        </Link>
                        {requester ? null : (
                          <button type="button" className="btn btn-outline btn-sm flex-1" onClick={() => handleEdit(item)}>
                            <Pencil className="mr-1 h-4 w-4" />
                            Editar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <FormModalLayout
          title={editingItem ? 'Editar processo' : 'Novo processo'}
          subtitle="Número, classificação, datas e vínculos com órgão, setor, servidor e requerente."
          headerIcon={FolderGit2}
          historyEntityType="processes"
          historyEntityId={editingItem?.id}
          onClose={() => {
            setShowModal(false);
            setEditingItem(null);
          }}
          size="lg"
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingItem(null);
                }}
                className="btn btn-outline"
              >
                Cancelar
              </button>
              <Can permission={P.PROCESS_ACCESS}>
                <button type="submit" form="processo-crud-form" className="btn btn-primary">
                  {editingItem ? 'Salvar' : 'Criar'}
                </button>
              </Can>
            </>
          }
        >
          <form id="processo-crud-form" onSubmit={handleSubmit} className="space-y-6">
            <FormSection
              id="proc-sec-id"
              title={t("common:identify")}
              description="Número, situação, tipo e data de abertura."
              icon={FolderGit2}
              variant="primary"
            >
              <div className="form-group">
                <label htmlFor="proc-numero" className="form-label form-label-required">
                  Número
                </label>
                <input
                  id="proc-numero"
                  type="text"
                  value={form.numero}
                  onChange={(e) => setForm((f) => ({ ...f, numero: e.target.value }))}
                  className="form-input"
                  placeholder="Ex: 2024/001"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="form-group">
                  <label htmlFor="proc-situacao" className="form-label">
                    Situação
                  </label>
                  <select
                    id="proc-situacao"
                    value={form.situacao}
                    onChange={(e) => setForm((f) => ({ ...f, situacao: Number(e.target.value) }))}
                    className="form-select"
                  >
                    {Object.entries(SITUACAO_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="proc-tipo" className="form-label">
                    Tipo
                  </label>
                  <select
                    id="proc-tipo"
                    value={form.tipo}
                    onChange={(e) => setForm((f) => ({ ...f, tipo: Number(e.target.value) }))}
                    className="form-select"
                  >
                    {Object.entries(TIPO_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="proc-data" className="form-label form-label-required">
                  Data de abertura
                </label>
                <input
                  id="proc-data"
                  type="date"
                  value={form.data_abertura}
                  onChange={(e) => setForm((f) => ({ ...f, data_abertura: e.target.value }))}
                  className="form-input max-w-xs"
                />
              </div>
            </FormSection>
            <FormSection
              id="proc-sec-vinc"
              title={t("common:links")}
              description="Opcionais. Use + para cadastrar e voltar com o registro já selecionado."
              icon={Link2}
              variant="slate"
            >
              <ProcessoVinculoFields
                idPrefix="proc"
                form={form}
                setForm={setForm}
                party={party}
                clientsQueryKey="identity-clients-processos-modal"
              />
            </FormSection>
            <FormSection
              id="proc-sec-desc"
              title={t("common:description")}
              description="Texto livre para observações do processo."
              icon={FileText}
              variant="emerald"
            >
              <div className="form-group">
                <label htmlFor="proc-desc" className="form-label">
                  Texto livre
                </label>
                <RichTextEditor
                  variant="compact"
                  value={form.descricao}
                  onChange={(v) => setForm((f) => ({ ...f, descricao: v }))}
                />
              </div>
            </FormSection>
          </form>
        </FormModalLayout>
      )}
    </FormPageShell>
  );
};

export default Processos;
